import { useState } from "react";
import { ArrowLeft, CheckCircle2, Phone, ShieldCheck, MessageSquare, Eye, FileText, AlertTriangle } from "lucide-react";

const ORG_NAME = "Rolling River Day Camp";
const ENROLL_URL = "https://rollingriver.campintouch.com";
const CAMP_EMAIL = "colleen@rollingriver.com";

/**
 * Public SMS Opt-In Consent Demonstration page.
 *
 * Purpose: show Twilio A2P 10DLC reviewers the exact opt-in flow used during camp enrollment
 * so they can verify the Call-to-Action (CTA). This page is INTENTIONALLY public (no login)
 * and contains everything reviewers need to approve the campaign:
 *   1. A live demo of the consent checkbox (visually identical to the one on CampMinder)
 *   2. The exact consent language
 *   3. Step-by-step explanation of the opt-in flow
 *   4. Sample messages
 *   5. Opt-out instructions (STOP, HELP)
 *   6. Links to full Terms and Privacy Policy
 */
export default function SmsOptIn() {
  const [agreed, setAgreed] = useState(false);
  const [phone, setPhone] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleDemoSubmit = (e) => {
    e.preventDefault();
    if (!agreed) return;
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="sms-optin-page">
      <header className="bg-emerald-900 text-white shadow-lg">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg sm:text-xl font-bold">{ORG_NAME}</h1>
            <p className="text-xs text-emerald-200">SMS Opt-In Consent</p>
          </div>
          <a href="/login" className="text-xs bg-emerald-700 hover:bg-emerald-600 px-3 py-1.5 rounded-lg font-medium inline-flex items-center gap-1" data-testid="back-to-app">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to App
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* ── 1. PURPOSE BANNER ── */}
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5" data-testid="reviewer-banner">
          <div className="flex items-start gap-3">
            <Eye className="w-6 h-6 text-amber-700 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-amber-900 mb-1">For Twilio Reviewers & Parents Verifying How We Collect SMS Consent</p>
              <p className="text-sm text-amber-800">
                This page demonstrates the exact consent flow used when a parent enrolls their child at {ORG_NAME}. Enrollment is handled by CampMinder (campintouch.com), which requires a login. This public page reproduces the consent checkbox so that reviewers and the public can verify our opt-in procedure without credentials.
              </p>
            </div>
          </div>
        </div>

        {/* ── 2. HERO ── */}
        <div className="bg-white rounded-2xl border-2 border-emerald-200 p-6 sm:p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Phone className="w-6 h-6 text-emerald-700" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">SMS Text Message Program</h2>
              <p className="text-sm text-gray-500">Operational notifications for enrolled camper families</p>
            </div>
          </div>
          <p className="text-base text-gray-700 leading-relaxed">
            {ORG_NAME} sends SMS text messages to parents and legal guardians of enrolled campers. Messages include daily attendance confirmations, absence alerts, early dismissal notices, bus arrival updates, weather-related closures, and pickup reminders. <strong>We do not send marketing or promotional content via SMS.</strong>
          </p>
        </div>

        {/* ── 3. CONSENT FLOW (STEPS) ── */}
        <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-700" /> How Parents Opt In (Step-by-Step)
          </h3>
          <ol className="space-y-4 text-sm text-gray-800">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-700 text-white font-bold flex items-center justify-center text-xs">1</span>
              <span>Parent visits <a href={ENROLL_URL} className="text-emerald-700 underline font-semibold" target="_blank" rel="noopener noreferrer">{ENROLL_URL}</a> and creates an enrollment account for their child.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-700 text-white font-bold flex items-center justify-center text-xs">2</span>
              <span>On the Contact Information page, the parent is prompted to provide a mobile phone number in a dedicated field labeled <strong>"Mobile Phone"</strong>.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-700 text-white font-bold flex items-center justify-center text-xs">3</span>
              <span>Directly below the phone field, the parent sees an <strong>unchecked</strong> consent checkbox with the exact wording shown in the demo form below.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-700 text-white font-bold flex items-center justify-center text-xs">4</span>
              <span>The parent <strong>must actively check the box</strong> to opt in. Consent is <strong>not required</strong> to complete enrollment — if the parent declines, {ORG_NAME} communicates by email only.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-700 text-white font-bold flex items-center justify-center text-xs">5</span>
              <span>Once checked and submitted, the phone number is added to the SMS notification list. The first message sent is a welcome / confirmation text with opt-out instructions.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-700 text-white font-bold flex items-center justify-center text-xs">6</span>
              <span>Parents can reply <strong>STOP</strong> at any time to unsubscribe or <strong>HELP</strong> for assistance.</span>
            </li>
          </ol>
        </div>

        {/* ── 4. LIVE DEMO CONSENT FORM (mirrors CampMinder's form) ── */}
        <div className="bg-white rounded-2xl border-2 border-emerald-300 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-700" /> Live Demo of the Consent Checkbox
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            This is a working demonstration of the exact consent checkbox shown during enrollment. It does not submit to our SMS system — this page is for reviewer verification only.
          </p>

          {submitted ? (
            <div className="bg-emerald-50 border-2 border-emerald-400 rounded-xl p-5 text-center" data-testid="demo-submitted">
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
              <p className="font-bold text-emerald-900">Demo consent recorded.</p>
              <p className="text-sm text-emerald-800 mt-1">In a real enrollment, this phone number would receive a welcome SMS within 1 minute. This is a demo only — no text has been sent.</p>
              <button
                type="button"
                onClick={() => { setSubmitted(false); setAgreed(false); setPhone(""); }}
                className="mt-4 text-sm bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 rounded-lg font-semibold"
                data-testid="demo-reset"
              >
                Reset demo
              </button>
            </div>
          ) : (
            <form onSubmit={handleDemoSubmit} className="space-y-4" data-testid="demo-form">
              <div>
                <label htmlFor="demo-phone" className="block text-sm font-semibold text-gray-700 mb-1">Mobile Phone</label>
                <input
                  id="demo-phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:border-emerald-500 focus:outline-none"
                  data-testid="demo-phone-input"
                />
              </div>

              <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4">
                <label className="flex gap-3 cursor-pointer" data-testid="demo-consent-label">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-1 w-5 h-5 text-emerald-600 accent-emerald-600 flex-shrink-0"
                    data-testid="demo-consent-checkbox"
                  />
                  <span className="text-sm text-gray-800 leading-relaxed">
                    <strong>I agree</strong> to receive text messages from <strong>{ORG_NAME}</strong> at the mobile number provided, including camp updates, camper notifications, attendance alerts, early dismissal notices, and pickup information. <strong>Message and data rates may apply.</strong> Message frequency varies and will not exceed approximately 10 messages per week. Reply <strong>STOP</strong> to unsubscribe or <strong>HELP</strong> for help. Consent is not a condition of enrollment. See our <a href="/sms-terms" className="underline text-emerald-700 font-semibold">SMS Terms</a> and <a href="/privacy-policy" className="underline text-emerald-700 font-semibold">Privacy Policy</a>.
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={!agreed || !phone}
                className={`w-full py-3 rounded-lg font-bold text-white transition-colors ${agreed && phone ? "bg-emerald-700 hover:bg-emerald-800" : "bg-gray-300 cursor-not-allowed"}`}
                data-testid="demo-submit-btn"
              >
                Submit (Demo Only)
              </button>
              {!agreed && <p className="text-xs text-gray-500 text-center">You must check the consent box to submit this demo.</p>}
            </form>
          )}
        </div>

        {/* ── 5. SAMPLE MESSAGES ── */}
        <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-sm" data-testid="sample-messages">
          <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-emerald-700" /> Sample Messages Parents Receive
          </h3>
          <div className="space-y-3">
            {[
              `${ORG_NAME}: Reminder — tomorrow is Color War! Campers should wear white. Reply STOP to unsubscribe, HELP for help. Msg & data rates may apply.`,
              `${ORG_NAME}: Jacob was marked absent today at 10:05 AM. If this is incorrect, please call the camp office. Reply STOP to unsubscribe.`,
              `${ORG_NAME}: Early dismissal today at 2:30 PM due to weather. Please arrive by 2:15. Reply STOP to unsubscribe, HELP for help.`,
            ].map((msg, i) => (
              <div key={i} className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-gray-800 font-mono leading-relaxed">
                {msg}
              </div>
            ))}
          </div>
        </div>

        {/* ── 6. OPT-OUT ── */}
        <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-600" /> How to Opt Out or Get Help
          </h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li><strong>Reply STOP</strong> (or STOPALL, UNSUBSCRIBE, CANCEL, END, QUIT) to any message to unsubscribe immediately. No further messages will be sent.</li>
            <li><strong>Reply HELP</strong> (or INFO) to any message for assistance.</li>
            <li>Email <a href={`mailto:${CAMP_EMAIL}`} className="text-emerald-700 underline font-semibold">{CAMP_EMAIL}</a> at any time to be removed manually.</li>
          </ul>
        </div>

        {/* ── 7. LINKS ── */}
        <div className="flex flex-wrap gap-3 justify-center pt-2" data-testid="policy-links">
          <a href="/sms-terms" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-emerald-300 bg-white text-emerald-900 font-semibold hover:bg-emerald-50">
            <FileText className="w-4 h-4" /> Full SMS Terms
          </a>
          <a href="/privacy-policy" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-emerald-300 bg-white text-emerald-900 font-semibold hover:bg-emerald-50">
            <ShieldCheck className="w-4 h-4" /> Privacy Policy
          </a>
        </div>

        <p className="text-center text-xs text-gray-400 pt-4">
          &copy; {new Date().getFullYear()} {ORG_NAME}. Mobile information and phone numbers are not shared with any third parties or affiliates for marketing purposes.
        </p>
      </main>
    </div>
  );
}
